<template>
  <div class="c-form-group">
    <div v-if="mainSection" class="main">
      <c-title v-if="mainSection.title" dark-text>
        {{ mainSection.title }}
      </c-title>

      <c-form-builder
        ref="mainForm"
        class="form"
        no-actions
        show-overflow
        no-bottom-shadow
        no-upper-shadow
        :disabled="disabled"
        :label-left="labelLeft"
        :fields-options="mainFieldsOptions"
        :schema="mainSection.fields"
        :value="mainForm"
        @input="setMainForm"
      >
        <slot name="main-section" />

        <slot
          slot="footer"
          name="main-footer"
        />

        <slot
          slot="actions"
          name="main-actions"
        />
      </c-form-builder>
    </div>

    <div class="groups">
      <div
        v-for="group in groups"
        :key="group.groupName"
        class="group"
      >
        <c-title
          v-if="group.groupLabel"
          class="title"
          dark-text
        >
          {{ group.groupLabel }}
        </c-title>

        <div class="forms">
          <div
            v-for="(form, index) in group.forms"
            :key="`${group.groupName}-form-${index}`"
            class="form-wrapper"
          >
            <div class="heading" :class="{ '-full-width': group.fullWidthHeader }">
              <c-flag
                :icon="group.headerIcon"
                class="flag"
                size="40"
                icon-size="18"
                grey
              />

              <span class="label">
                {{ index + 1 }}{{ group.numeralLetter || 'ª'}} {{ group.headerLabel }}
              </span>

              <transition name="fade">
                <c-button
                  v-if="isItemDeletable(group)"
                  class="btn-delete"
                  flat
                  error
                  icon="trash"
                  icon-size="18"
                  size="30"
                  :disabled="disabled || group.disabled"
                  @click="handleDeleteGroupItem(group, index)"
                />
              </transition>

              <slot
                :name="`${group.groupName}-delete-modal`"
                :attrs="{ isOpened: showDeleteModal(group.groupName, index), form }"
                :listeners="{
                  cancel: () => { isDeletingGroupItem = null },
                  delete: () => { deleteGroupItem(...isDeletingGroupItem) },
                }"
              />
            </div>

            <c-form-builder
              ref="groupForms"
              class="form"
              no-actions
              show-overflow
              no-bottom-shadow
              no-upper-shadow
              :label-left="labelLeft"
              :schema="group.fields"
              :fields-options="getFieldOptions(group, form)"
              :disabled="disabled || group.disabled"
              :value="form"
              @input="setGroupForm(group, index, $event)"
            >
              <template
                v-for="slot in group.slots"
                :slot="slot"
                slot-scope="fieldProps"
              >
                <slot
                  :name="`${group.groupName}-${slot}`"
                  v-bind="fieldProps"
                />
              </template>

              <template v-for="(_, fieldName) in group.fields">
                <slot
                  :slot="`${fieldName}-divider`"
                  :name="`${group.groupName}-${fieldName}-divider`"
                />
              </template>

              <slot :name="group.groupName" />

              <slot
                slot="footer"
                :name="`${group.groupName}-footer`"
              />

              <slot
                slot="actions"
                :name="`${group.groupName}-actions`"
              />
            </c-form-builder>
          </div>
        </div>

        <c-add
          class="add-box"
          :text="group.addBtnLabel"
          :icon-only="!group.addBtnLabel"
          :mobile-icon-only="!group.addBtnLabel"
          :disabled="disabled || group.disabled || shouldDisableAddButton(group)"
          @add="addFormToGroup(group)"
        />

        <c-validation-message
          v-if="!!groupsValidationMessage[group.groupName]"
          :key="`validation-message-${group.groupName}`"
          class="validation-message"
        >
          {{ group.maxItemsMessage }}
        </c-validation-message>
      </div>
    </div>

    <slot name="after-groups" />

    <div v-if="!noActions" class="actions">
      <slot name="actions">
        <c-button primary class="action">
          Salvar
        </c-button>
      </slot>
    </div>
  </div>
</template>

<script>
import CTitle from '@convenia/components/CTitle'
import CFormBuilder from '@convenia/components/CFormBuilder/index.vue'
import CFlag from '@convenia/components/CFlag'
import CButton from '@convenia/components/CButton'
import CAdd from '@convenia/components/CAdd'
import CValidationMessage from '@convenia/components/CValidationMessage'
import FormGroupMixin from './mixins/FormGroupMixin'

export default {
  name: 'CFormGroup',
  mixins: [ FormGroupMixin() ],

  components: {
    CFormBuilder,
    CTitle,
    CFlag,
    CAdd,
    CButton,
    CValidationMessage
  },

  props: {
    value: {
      type: Object,
      default: () => ({})
    },
  },

  computed: {
    formData: {
      get () {
        return this.value
      },
      set (newData) {
        this.emitInput(newData)
      }
    },
  },

  methods: {
    emitInput (payload) {
      this.$emit('input', payload)
    },

    async onSubmit () {
      const { mainForm, groupForms } = this.$refs
      const allForms = [
        ...(this.mainSection && mainForm ? [ mainForm ] : []),
        ...groupForms || []
      ]

      const isValid = (await Promise.all(allForms
        .map(form => form.onSubmit()))).every(Boolean)

      if (isValid) this.$emit('submit', this.formData)

      return this.formData
    },
  }
}
</script>

<style lang="scss">
@import './style.scss';
</style>
